/**
 * Top-level chrome for the admin SPA.
 *
 * Layout:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │  Sidebar  │  Main content area (Outlet)              │
 *   │  (logo +  │                                          │
 *   │   nav)    │                                          │
 *   └───────────┴──────────────────────────────────────────┘
 *
 * The sidebar holds the brand mark + nav links to Devices, Releases,
 * Customers, and Audit. Devices is the only fully wired route in Phase 1;
 * the rest land on the placeholder page.
 */

import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getAdminHealth, IS_DEV_API } from "@/lib/api";

const NAV_ITEMS: ReadonlyArray<{
  to: string;
  label: string;
  badge?: string;
  /** When set, nav is active for any path starting with this prefix. */
  matchPrefix?: string;
}> = [
  { to: "/devices", label: "Devices" },
  { to: "/releases", label: "Releases" },
  { to: "/customers", label: "Customers" },
  { to: "/audit", label: "Audit" },
  { to: "/stream/devices", label: "RallyStream", matchPrefix: "/stream" },
];

function systemStatusTone(
  data: Awaited<ReturnType<typeof getAdminHealth>> | undefined,
  failed: boolean,
): {
  label: string;
  dot: string;
} {
  if (failed) return { label: "Health check failed", dot: "bg-rose-500" };
  if (!data) return { label: "Checking…", dot: "bg-amber-400" };
  if (!data.db.reachable) return { label: "Database unreachable", dot: "bg-rose-500" };
  if (!data.r2.reachable) return { label: "R2 issue", dot: "bg-rose-500" };
  const soft =
    (!data.stripe.reachable && !data.stripe.error?.includes("unconfigured")) ||
    (!data.hologram.reachable && !data.hologram.error?.includes("unconfigured"));
  if (soft) return { label: "Upstream degraded", dot: "bg-amber-400" };
  return { label: "All checks OK", dot: "bg-emerald-400" };
}

export default function Layout() {
  const location = useLocation();
  const healthQ = useQuery({
    queryKey: ["admin-health"],
    queryFn: getAdminHealth,
    staleTime: 45_000,
    retry: 1,
  });
  const status = systemStatusTone(healthQ.data, healthQ.isError);

  return (
    <div className="flex min-h-screen bg-surface-sunken">
      <aside className="w-60 shrink-0 border-r border-surface-border bg-surface flex flex-col">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-surface-border">
          <img
            src="/rallycomm-mark.svg"
            alt="RallyComm"
            className="h-9 w-9"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold tracking-wide text-slate-100">
              RallyComm
            </span>
            <span className="text-xs uppercase tracking-widest text-brand-orange">
              Fleet&nbsp;Admin
            </span>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={() => {
                const hit = item.matchPrefix
                  ? location.pathname.startsWith(item.matchPrefix)
                  : location.pathname === item.to ||
                    (item.to !== "/" && location.pathname.startsWith(`${item.to}/`));
                return [
                  "flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors",
                  hit
                    ? "bg-brand-orange/15 text-brand-orange ring-1 ring-inset ring-brand-orange/40"
                    : "text-slate-300 hover:bg-surface-raised hover:text-slate-50",
                ].join(" ");
              }}
            >
              <span>{item.label}</span>
              {item.badge ? (
                <span className="text-[10px] uppercase tracking-wider text-brand-dim">
                  {item.badge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-surface-border px-5 py-4 text-[11px] text-brand-dim space-y-2">
          <div className="flex items-center gap-2">
            <span className={`inline-block h-2 w-2 rounded-full ${status.dot}`} />
            <span className="uppercase tracking-wider text-slate-200">
              System status
            </span>
            <span className="text-brand-dim normal-case">— {status.label}</span>
          </div>
          {IS_DEV_API ? (
            <div className="flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
              <span className="uppercase tracking-wider text-amber-300">
                Dev mode
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
              <span className="uppercase tracking-wider text-emerald-300">
                Cloudflare Access
              </span>
            </div>
          )}
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
